import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoaderService } from '../../../shared/components/loader/loader.service';
import { ProfileBllService } from '../profile-bll.service';
import { debounceTime } from 'rxjs';
import stateDistrict from '../../../../assets/state_districts_data/state_districts.json';
import { TldService } from '../../../core/services/tld.service';
import { APIErrorModel, BaseResponse } from '../../../shared/models/base-response.model';
import { GoogleJwtPayload, UpdateProfileRequestModel, UserEventStatusRespModel } from '../profile.model';
import { environment } from '../../../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LeftSideNavComponent } from '../../../shared/layout/app/left-menu/left-side-nav.component';
import { HeaderComponent } from '../../../shared/layout/app/header/header.component';
import { UserService } from '../../../core/services/user.service';
import { HomeBllService } from '../../home/home-bll.service';
import { EventDetailsResponseModel } from '../../home/home.model';
declare const google: any;

@Component({
  selector: 'app-edit-basic-profile',
  imports: [ReactiveFormsModule, CommonModule, LeftSideNavComponent, HeaderComponent],
  templateUrl: './edit-basic-profile.component.html',
  styleUrls: ['./edit-basic-profile.component.scss']
})
export class EditBasicProfileComponent {
  /**
   * Form group for registration
   */
  registerForm!: FormGroup;

  /**
   * variable to hold the email source
   * invalid image flag
   */
  email_source: string = '';
  invalidImage: string = '';
  isEmailFetchedFromGoogle: boolean = false;
  googleIdToken: string = '';
  emailField: string = '';

  /**
   * Flag to indicate if the email is already used
  */
  isEmailAlreadyExist: boolean = false;
  isValidEmail: boolean = false;
  isLoadingEmailCheck: boolean = false;

  /**
    * State and district data for dropdowns
    * This data is imported from a JSON file containing state and district mappings.
   */
  state_District: { [key: string]: string[] } = stateDistrict;
  states: string[] = Object.keys(this.state_District);
  cities: string[] = [];

  /**
   * Selected file for profile logo
   */
  selectedFile: File | null = null;  // Can be a File or null
  previewLogo: string = '/assets/images/Default-profile-photo.png'; // String path/URL
  allowedFileTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg']; // Array of strings
  maxFileSize: number = 15 * 1024 * 1024; // Number (15 MB in bytes)
  logoTitle: string = ''; // String (title or name for the logo)
  private fileInputRef: HTMLInputElement | null = null;

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel['user_profile'] | null = null;

  returnUrl: string = '/home'; // Default return URL
  showSuccessMessage = false; // Flag to show success message
  isLinkLoginFlow = false;

  eventDetails: EventDetailsResponseModel | null = null;

  /**
   * Constructor for EditBasicProfileComponent
   * @param fb FormBuilder
   * @param loader LoaderService
   * @param profileBll ProfileBllService
   * @param tldService TldService
   */
  constructor(private fb: FormBuilder,
    private loader: LoaderService,
    private profileBll: ProfileBllService,
    private tldService: TldService,
    private snackBar: MatSnackBar,
    private router: Router,
    private userService: UserService,
    private bllService: HomeBllService
  ) {
    /**
     * Capture returnUrl from navigation state if available
     */
    const nav = this.router.getCurrentNavigation();
    this.returnUrl = nav?.extras.state?.['returnUrl'] || '/home';
    const linkLoginRedirect = this.profileBll.getLinkLoginRedirect();
    if(linkLoginRedirect) {
      this.isLinkLoginFlow = true;
    }
  }

  /**
   * Component initialization
   */
  ngOnInit(): void {
    this.registerForm = this.fb.group({
      profilePic: [''],
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(60),
        this.profileBll.nameValidator(), // Custom validator for name
        this.profileBll.startsWithLetterValidator() // Custom validator to check if starts with letter
      ]),

      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(60),
        this.profileBll.nameValidator(), // Custom validator for lastName
        this.profileBll.startsWithLetterValidator() // Custom validator to check if starts with letter
      ]),

      email: new FormControl('', [
        Validators.minLength(7),
        Validators.maxLength(254), // 254 is the official max email length
        this.profileBll.emailFormatValidator()
      ]),
      state: ['', Validators.required],
      city: [{ value: '', disabled: true }, Validators.required], // initially disabled
      whatsAppConsent: [false] // New checkbox for WhatsApp consent
    });


    /**
    * Update city options when state changes.
    */
    this.registerForm.get('state')?.valueChanges.subscribe(state => {
      this.updateCityOptions(state);
      this.registerForm.get('city')?.setValue('');
    });

    /**
     * Email field value changes handling with debounce
     * - Trims leading spaces
     * - Enforces max length of 254 characters
     */
    const emailControl = this.registerForm.get('email');
    if (emailControl) {
      emailControl.addValidators(this.profileBll.emailTldValidator(this.tldService));
      emailControl.updateValueAndValidity();

      emailControl.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => {
          let email = emailControl.value || '';

          //Truncate if exceeds 254 chars BEFORE validation
          if (email.length > 254) {
            email = email.substring(0, 254);
          }

          // Update without triggering valueChanges again
          if (email !== emailControl.value) {
            emailControl.setValue(email, { emitEvent: false });
          }

          // Reset flags
          this.resetFlags();

          // Call API only if valid
          if (email && emailControl.valid && this.userProfile?.['email']?.trim().toLowerCase() !== email.trim().toLowerCase()) {
            this.callCheckEmailStatus(email);
          }
        });
    }

    // Fetch user profile and event status on init
    this.fetchUserEventStatus();
    this.loadEventDetails();
  }

  /** Reusable method to enable/disable city based on state */
  private updateCityOptions(state: string) {
    this.cities = this.state_District[state] || [];
    const cityControl = this.registerForm.get('city');
    if (this.cities.length > 0) {
      cityControl?.enable();
    } else {
      cityControl?.disable();
    }
  }

  /**
   * Custom validator to check if the name contains only valid characters
   * @returns Validator function
   */
  restrictInvalidChars(event: KeyboardEvent): void {
    const key = event.key;

    const allowedSpecialChars = ['.', '-', '\'', '’'];
    const isLetter = /^[\p{L}]$/u.test(key); // Unicode letter
    const isAllowedSpecialChar = allowedSpecialChars.includes(key);
    const isSpace = key === ' ';

    if (!isLetter && !isAllowedSpecialChar && !isSpace) {
      event.preventDefault(); // Block disallowed characters
    }
  }

  /**
   * Handle paste event to remove consecutive spaces
   * @param event ClipboardEvent
   * @param controlName Name of the form control
   * @param maxLength Maximum length for the input
   */
  onPasteRemoveConsecutiveSpaces(event: ClipboardEvent, controlName: string, maxLength: number): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanedText = pastedText.trim().replace(/\s{2,}/g, ' ');

    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    const newValue =
      currentValue.slice(0, selectionStart) +
      cleanedText +
      currentValue.slice(selectionEnd);

    const truncatedValue = newValue.slice(0, maxLength);
    input.value = truncatedValue;

    const control = this.registerForm.get(controlName);
    if (control) {
      control.setValue(truncatedValue);
    }

    const cursorPos = Math.min(selectionStart + cleanedText.length, maxLength);
    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }

  /**
   * Prevent leading space and consecutive spaces in input
   * @param event KeyboardEvent
   */
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ') {
      const cursorPos = input.selectionStart || 0;

      // Prevent space at start
      if (cursorPos === 0) {
        event.preventDefault();
        return;
      }
      // Prevent consecutive space
      // Check character immediately before the cursor
      if (cursorPos > 0 && input.value.charAt(cursorPos - 1) === ' ') {
        event.preventDefault();
      }
    }
  }

  /**
   * Prevent space in email input
   * @param event KeyboardEvent
   */
  preventAllSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key === ' ') {
      event.preventDefault(); // block all spaces
    }
  }

  /**
   * Enforce maximum length for input
   * @param event InputEvent
   * @param maxLength Maximum length for the input
   */
  enforceMaxLength(event: Event, maxLength: number): void {
    const input = event.target as HTMLInputElement;

    if (input.value.length > maxLength) {
      input.value = input.value.substring(0, maxLength);

      // Ensure Angular FormControl reflects the trimmed value
      input.dispatchEvent(new Event('input'));
    }
  }

  onEmailTypeChange(event: any): void {
    this.email_source = 'manual'; // Reset to manual if user changes email type
    this.isEmailFetchedFromGoogle = false; // Reset Google email fetch flag
  }

  /**
 * Handle Google sign-in button click
 */
  onGoogleIconClick() {
    // Handle Google sign-in button click
    this.isEmailFetchedFromGoogle = true;
    google.accounts.id.cancel(); //  prevent abort from earlier session

    google.accounts.id.initialize({
      client_id: environment.google_client_id,
      callback: this.handleGoogleCallback.bind(this),
      ux_mode: 'popup',
    });

    google.accounts.id.prompt();
  }

  /**
   * Handle the Google callback after sign-in
   * @param response Response from Google sign-in
   */
  handleGoogleCallback(response: any) {
    try {
      this.googleIdToken = response.credential;
      const decoded = jwtDecode<GoogleJwtPayload>(this.googleIdToken);
      console.log('Decoded Google ID Token:', decoded);
      this.handleGoogleUserInfo(decoded);
    } catch (err) {
      console.error('Failed to decode Google token', err);
    }
  }

  /**
  * Handle the Google user information after sign-in
  * @param userInfo User information from Google
  */
  handleGoogleUserInfo(userInfo: GoogleJwtPayload) {
    this.registerForm.patchValue({
      email: userInfo.email,
      firstName: userInfo.given_name,
      lastName: userInfo.family_name,
    });
    if (userInfo.picture && this.selectedFile === null) {
      this.previewLogo = userInfo.picture;
    }

    this.email_source = 'google';
    this.isEmailFetchedFromGoogle = true;
  }

  /**
   * Handle profile logo selection
   * @param event Change event from file input
   */
  onProfileLogoSelected(event: Event): void {
    this.invalidImage = ''; // Reset invalid image message
    const input = event.target as HTMLInputElement;
    this.fileInputRef = input; //reset later
    if (!input.files || input.files.length === 0) return;

    this.selectedFile = input.files[0];

    // Validate file type
    if (!this.allowedFileTypes.includes(this.selectedFile.type)) {
      this.resetFileInput();
      this.invalidImage = 'Invalid file type.';
      return;
    }

    // Validate file size
    if (this.selectedFile.size > this.maxFileSize) {
      this.resetFileInput();
      this.invalidImage = 'Please select an image smaller than 15 MB.';
      return;
    }

    // Normalize file name
    const fileName = this.selectedFile.name;
    const fileExt = fileName.split('.').pop()?.toLowerCase() ?? '';
    const baseName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
    this.logoTitle = `${baseName}.${fileExt}`;

    // Preview image
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        this.previewLogo = result;
      }
    };
    reader.readAsDataURL(this.selectedFile);
  }

  /**
 * Reset the file input and preview
 */
  resetFileInput() {
    this.selectedFile = null;
    this.previewLogo = '/assets/images/Default-profile-photo.png';
    if (this.fileInputRef) {
      this.fileInputRef.value = '';
    }
    setTimeout(() => {
      this.invalidImage = '';
    }, 3000);
  }

  /** Fetch user profile and event status */
  fetchUserEventStatus() {
    this.loader.show();
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          this.registerForm.patchValue({
            firstName: this.userProfile.first_name ?? '',
            lastName: this.userProfile.last_name ?? '',
            email: this.userProfile.email ?? '',
            state: this.userProfile.state ?? '',
            city: this.userProfile.city ?? '',
            whatsAppConsent: this.userProfile.whatsapp_opt_in_status ?? false
          });

          if (this.userProfile.image_url) {
            this.previewLogo = this.userProfile.image_url ?? "/assets/images/Default-profile-photo.png";
          }
          
          let userName =
          res.data.user_profile.first_name +
          " " +
          res.data.user_profile.last_name;
        this.userService.setUser({
          name: userName, // get from API or JWT
          profileImg: this.previewLogo, // fallback if empty
          mobile : res.data.user_profile.mobile,
          state: res.data.user_profile.state,
          city : res.data.user_profile.city,
          hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
          hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
        });

        }
        this.loader.hide();
      })
      .catch((err: APIErrorModel) => {
        this.loader.hide();
        console.error("Error fetching user event status:", err);
        alert("Failed to fetch user event status. Please try again.");
      });
  }

  /**
   * Handle profile logo selection
   * @param selectedFile File selected for upload
   * Initiates the process to get a signed URL and upload the image to S3
   * After successful upload, it updates the user profile with the new image reference
   */
  callgetImageUploadLink(
    selectedFile: File,
  ) {
    this.profileBll.submitGetUploadImageLink(selectedFile.name)
      .then((res) => {
        const file_name_with_extension = res.data?.file_name_with_extension;
        const mediaDetails = res.data?.s3_media_post;

        if (mediaDetails?.url && mediaDetails?.fields) {
          this.callUploadImageToS3(mediaDetails.url, mediaDetails.fields, selectedFile, file_name_with_extension);
        }

      }).catch((err: APIErrorModel) => {
        this.loader.hide();
        alert("Failed to get upload image link. Please try again.");
      })
  }

  /**
   * Handle image upload to S3
   * @param url S3 upload URL
   * @param fields S3 upload fields
   * @param file File to upload
   * @param file_name_with_extension File name with extension
   */
  callUploadImageToS3(url: string, fields: any, file: File, file_name_with_extension?: string) {
    this.profileBll.uploadImageToS3(url, fields, file)
      .then(() => {
        this.callUpdateUserProfile(file_name_with_extension);
      }).catch((err: APIErrorModel) => {
        this.loader.hide();
        alert("Failed to upload image. Please try again.");
      });
  }

  /**
   * Check email status
   * @param email Email to check
   * Calls the API to check if the email is already registered or usable
   * Updates flags based on the API response
   */
  callCheckEmailStatus(email: string) {
    this.isLoadingEmailCheck = true; // Set loading state
    this.resetFlags(); // Reset flags before making the API call
    this.profileBll.submitCheckEmailStatus(email)
      .then((response) => this.handleCheckEmailStatusSuccess(response))
      .catch((err: APIErrorModel) => this.handleCheckEmailStatusError(err))
      .finally(() => this.loader.hide());
  }

  /**
     * Handle successful response from Check Email Status API
     * @param response Response from the API
     */
  handleCheckEmailStatusSuccess(response: any) {
    console.log("Email status checked successfully.");
    this.isEmailAlreadyExist = false;
    this.isValidEmail = true;
    this.isLoadingEmailCheck = false;
  }

  /**
   * Handle error response from Check Email Status API
   * @param err Error object from the API
   */
  handleCheckEmailStatusError(err: APIErrorModel): void {
    if (err.status === 409) {
      this.isLoadingEmailCheck = false;
      this.isEmailAlreadyExist = true;
      this.isValidEmail = false;
    }
  }

  /**
   * Handle form submission
   */
  onSubmit() {
    const currentValues = this.registerForm.value;
    // Trim all string fields
    const trimmedCurrentValues = {
      firstName: (currentValues.firstName || '').trim(),
      lastName: (currentValues.lastName || '').trim(),
      email: (currentValues.email || '').trim(),
      state: (currentValues.state || '').trim(),
      city: (currentValues.city || '').trim(),
      whatsAppConsent: currentValues.whatsAppConsent ?? false
    };
    // Check if form values are unchanged AND no new profile picture
    const isUnchanged =
      trimmedCurrentValues.firstName === this.userProfile?.first_name &&
      trimmedCurrentValues.lastName === this.userProfile?.last_name &&
      trimmedCurrentValues.email === this.userProfile?.email &&
      trimmedCurrentValues.state === this.userProfile?.state &&
      trimmedCurrentValues.city === this.userProfile?.city &&
      trimmedCurrentValues.whatsAppConsent === this.userProfile?.whatsapp_opt_in_status &&
      !this.selectedFile;

    // If the form is pristine (no changes), do nothing
    if (isUnchanged) {
      console.log('No changes detected, skipping API call.');
      if (this.isLinkLoginFlow) {
        this.profileBll.redirectToLinkLogin();
      }
      else{
        this.router.navigateByUrl(this.returnUrl);
      }
      
      return;
    }

    this.loader.show();

    if (this.selectedFile) {
      this.callgetImageUploadLink(this.selectedFile);
    } else {
      this.callUpdateUserProfile();
    }

  }


  /**
   * Update user profile
   * @param file_name_with_extension Optional file name with extension for profile picture
   * Calls the API to update the user profile with the provided details
   * If a file name is provided, it includes it in the update payload
   */
  callUpdateUserProfile(file_name_with_extension?: string) {
    const payload: UpdateProfileRequestModel = {
      first_name: this.registerForm.get('firstName')?.value.trim(),
      last_name: this.registerForm.get('lastName')?.value.trim(),
      state: this.registerForm.get('state')?.value,
      city: this.registerForm.get('city')?.value,
      whatsapp_opt_in_status: !!this.registerForm.get('whatsAppConsent')?.value
    };
    const emailControl = this.registerForm.get('email')?.value ?? '';
    const originalEmail = this.userProfile?.['email'] ?? '';

    const wasPreviouslyGoogle = this.userProfile?.is_email_verified === true;
    const isGoogleLogin = this.email_source === 'google' && this.googleIdToken;
    const isEmailChanged = emailControl && emailControl !== originalEmail;

    // Manual Email Change
    if (isEmailChanged) {
      payload['email'] = emailControl;
      payload['email_source'] = this.email_source;
    }

    // Continue with Google
    if (isGoogleLogin && (isEmailChanged || !wasPreviouslyGoogle)) {
      payload['email'] = emailControl || originalEmail;
      payload['email_source'] = 'google';
      payload['google_id_token'] = this.googleIdToken;
    }

    if (file_name_with_extension) {
      payload['profile_file_name_with_extension'] = file_name_with_extension;
    }

    this.profileBll.submitUpdateProfile(payload).then((res) => {
      if (res) {
        this.loader.hide();
        this.showSuccessMessage = true; // Show success message 
        setTimeout(() => {
          if (this.isLinkLoginFlow) {
            this.profileBll.redirectToLinkLogin();
          }
          else{
            this.router.navigateByUrl(this.returnUrl);
          }
        }, 2000);
      }
    }).catch((err: APIErrorModel) => {
      this.loader.hide();
      this.snackBar.open('Failed to update profile', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    });
  }

  /**
 * Reset flags for email status checks
 */
  resetFlags() {
    this.isEmailAlreadyExist = false;
    this.isValidEmail = false;
  }

  /**
   * Handle back button click
   * Navigates to the returnUrl or home if not specified
   */
  onBackBtnClick() {
    if (this.isLinkLoginFlow) {
      this.profileBll.redirectToLinkLogin();
    }
    else{
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  loadEventDetails() {
    this.bllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}

